import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import useR05Api from '../api/useR05Api';

import i18n from '../assets/languages/configs';
import FujiModal from '../components/fuji-modal/FujiModal';
import { canDisplayNow, getValueByLanguage, isEmptyObject, isReserveTicket, showNotificationDialog } from '../utils';
import { QR_TYPE, TIMEOUT_DURATION } from '../utils/Constants';
import useDevice from './useDevice';
import { ErrorType, MultilingualType, OrderTicket, OrderTicketList } from 'types';

/**
 * 企画券を有効化するためのカスタムフック。
 */
const useActivateTicket = () => {
    const [loading, setLoading] = useState(false);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [orderTicket, setOrderTicket] = useState<OrderTicket>();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { deviceId } = useDevice();
    const { fetchOrderDetail, activateTicket: usageTicket, terminateTicket: terminateOrderTicket } = useR05Api();
    const { language } = i18n;
    const l = useCallback((text?: MultilingualType | null) => getValueByLanguage(text, language), [language]);

    const showErrorNotification = useCallback(
        (errorText: string) => {
            showNotificationDialog('error', '', t('notification.title'), t(errorText));
        },
        [t],
    );

    /**
     * エラーが発生したら、Notificationにてユーザーに通知する。
     * @param {Error} error
     */
    const showActivateErrorNotification = useCallback(
        (error: ErrorType) => {
            switch (error.errorType) {
                case 'out_of_date_order': //使用可能期限切れ
                    showErrorNotification(t('error-message.activation.out-of-date'));
                    break;
                case 'cannot_activate_order_today': // 有効化不可曜日エラー有効化不可日エラー
                case 'cannot_activate_order_holiday':
                case 'today_is_exceptional_day':
                    showErrorNotification(t('error-message.activation.disable-date'));
                    break;
                case 'cannot_activate_order_at_now': // 有効化可能時間外エラー
                    showErrorNotification(t('error-message.activation.disable-daytime'));
                    break;
                case 'undefined_parameter': //4xxエラー
                case 'not_found_order_ticket_id':
                case 'order_status_is_not_bought':
                case 'not_found_bought_ticket':
                case 'invalid_parameter':
                case 'out_of_date_token':
                case 'invalid_signature_token':
                case 'invalid_api_key':
                    showErrorNotification(t('error-message.activation.failed'));
                    break;
                case 'unexpected': //5xxエラー
                    showErrorNotification(t('error-message.activation.unexpected-error'));
                    break;

                default:
                    showErrorNotification(t('error-message.activation.failed'));
                    break;
            }
        },
        [showErrorNotification, t],
    );

    /**
     * 有効化処理
     * チェック・タイムアウト・エラーダイアログ表示・遷移まで
     * @param {Object} orderTicket
     */
    const activateTicket = useCallback(
        async (orderTicket?: OrderTicket) => {
            setIsConfirmModalOpen(false);

            if (navigator.onLine) {
                if (!orderTicket) {
                    showErrorNotification(t('error-message.activation.failed'));
                    return;
                }
                let timeoutId;

                try {
                    timeoutId = setTimeout(() => {
                        setLoading(false);
                        showErrorNotification(t('error-message.activation.timeout-error'));
                    }, TIMEOUT_DURATION);

                    setLoading(true);
                    await usageTicket({
                        orderTicketId: orderTicket.orderTicketId,
                        deviceId: deviceId,
                    });
                    clearTimeout(timeoutId);
                    setLoading(false);
                    navigate('/bought/ticket', {
                        state: { orderId: orderTicket.orderId, orderTicketId: orderTicket.orderTicketId },
                    });
                } catch (error) {
                    clearTimeout(timeoutId);
                    setLoading(false);
                    showActivateErrorNotification(error as ErrorType);
                }
            } else {
                showErrorNotification(t('error-message.server-error'));
            }
        },
        [deviceId, navigate, showActivateErrorNotification, showErrorNotification, t, usageTicket],
    );

    /**
     * チケットが有効化できるかチェックする。
     * @param {Object} ticket
     */
    const checkActivateTicket = useCallback(
        async (orderTicket: OrderTicket | OrderTicketList) => {
            if (navigator.onLine) {
                if (orderTicket == null) {
                    showErrorNotification(t('error-message.tickets.fetch-info-failed'));
                    return;
                }
                let timeoutId;
                let isTimeout = false;

                try {
                    timeoutId = setTimeout(() => {
                        setLoading(false);
                        showErrorNotification(t('error-message.tickets.fetch-info-failed'));
                        isTimeout = true;
                    }, TIMEOUT_DURATION);
                    setLoading(true);
                    const data = await fetchOrderDetail({
                        orderId: orderTicket.orderId,
                        orderTicketId: orderTicket.orderTicketId,
                    });
                    clearTimeout(timeoutId);
                    setLoading(false);
                    if (data == null) {
                        return;
                    }
                    if (!isTimeout) {
                        const orderTicketData = { ...data, ...data.comboItems[0] };
                        //チケットが有された場合はチケット表示画面に遷移する
                        const canDisplayTicket = canDisplayNow(orderTicket);

                        if (canDisplayTicket || orderTicketData?.qrType === QR_TYPE.SINGLE_SCAN) {
                            navigate('/bought/ticket', {
                                state: {
                                    orderId: orderTicket.orderId,
                                    orderTicketId: orderTicket?.orderTicketId ?? null,
                                },
                            });
                            return;
                        }
                        if (isReserveTicket(orderTicketData)) {
                            void activateTicket(orderTicketData);
                        } else {
                            setIsConfirmModalOpen(true);
                        }
                        setOrderTicket(orderTicketData);
                    }
                } catch {
                    clearTimeout(timeoutId);
                    setLoading(false);
                    showErrorNotification(t('error-message.tickets.fetch-info-failed'));
                }
            } else {
                showErrorNotification(t('error-message.server-error'));
            }
        },
        [activateTicket, fetchOrderDetail, navigate, showErrorNotification, t],
    );

    /**
     * チケットを使う（モーダル確認）
     */
    const confirmActivateTicket = useCallback(() => {
        if (isReserveTicket(orderTicket)) {
            void activateTicket(orderTicket);
        } else {
            setIsConfirmModalOpen(true);
        }
    }, [activateTicket, orderTicket]);

    const confirmModalClose = useCallback(() => {
        setIsConfirmModalOpen(false);
    }, []);

    /**
     * 確認モーダル
     */
    const ActivateConfirmModal = useCallback(() => {
        const confirmMessage =
            isEmptyObject(orderTicket) || isEmptyObject(orderTicket?.activateMessage)
                ? t('modal.activate-confirmation')
                : l(orderTicket?.activateMessage);
        return (
            <FujiModal
                isOpen={isConfirmModalOpen}
                onCancel={confirmModalClose}
                txtCancel={t('modal.activate-cancel') ?? ''}
                onSubmit={() => void activateTicket(orderTicket)}
                txtSubmit={t('modal.activate-use')}
                body={<div style={{ color: 'red' }}>{confirmMessage}</div>}
            />
        );
    }, [activateTicket, confirmModalClose, isConfirmModalOpen, l, orderTicket, t]);
    /**
     * チケット使用終了処理
     * @param {Object} orderTicket
     */
    const terminateTicket = useCallback(
        async (orderTicket?: OrderTicket) => {
            if (navigator.onLine) {
                if (!orderTicket) {
                    showErrorNotification(t('error-message.terminate.failed'));
                    return;
                }
                let timeoutId;

                try {
                    timeoutId = setTimeout(() => {
                        setLoading(false);
                        showErrorNotification(t('error-message.activation.timeout-error'));
                    }, TIMEOUT_DURATION);

                    setLoading(true);
                    await terminateOrderTicket(
                        {
                            orderTicketId: orderTicket.orderTicketId,
                        },
                        TIMEOUT_DURATION,
                    );
                    clearTimeout(timeoutId);
                    setLoading(false);
                    showNotificationDialog(
                        'success',
                        'terminate-order-ticket',
                        t('notification.title'),
                        t('notification.terminate-order-ticket-success'),
                    );
                    navigate('/bought');
                } catch {
                    clearTimeout(timeoutId);
                    setLoading(false);
                    showErrorNotification(t('error-message.terminate.failed'));
                }
            } else {
                showErrorNotification(t('error-message.terminate.failed'));
            }
        },
        [navigate, showErrorNotification, t, terminateOrderTicket],
    );

    return {
        loading,
        checkActivateTicket,
        confirmActivateTicket,
        activateTicket,
        ActivateConfirmModal,
        terminateTicket,
    };
};

export default useActivateTicket;
